import React from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Handshake, Heart } from 'lucide-react';
import styles from './About.module.css';

import fotoZaidan from '../assets/foto-zaidan.jpg';
import fotoAmira from '../assets/foto-amira.jpeg';
import fotoTriarko from '../assets/foto-triarko.jpg';
import fotoAdnan from '../assets/foto-adnan.jpg';
import fotoKenez from '../assets/foto-kenez.jpg';
import fotoMonica from '../assets/foto-monica.JPG';
import fotoMorin from '../assets/foto-morin.jpg';
import fotoLintang from '../assets/foto-lintang.jpeg';
import fotoMaria from '../assets/foto-maria.jpg';

import logoYouth from '../assets/logo-youth-in-public-health.png';
import logoAnara from '../assets/logo-anara.png';
import logoSDGs from '../assets/logo-sdgs-hub-ui.png';
import logoKemensos from '../assets/logo-kemensos-ri.png';
import logoChildFund from '../assets/logo-childfund.png';

// Pimpinan (ditonjolkan)
const LEADS = [
  { name: 'Amira Puji Hastuti', role_id: 'Project Leader', role_en: 'Project Leader', photo: fotoAmira },
  { name: 'Dr. Triarko Nurlambang, MA.', role_id: 'Supervisor', role_en: 'Supervisor', photo: fotoTriarko },
  { name: 'Adnan Hasyim Wibowo', role_id: 'Supervisor', role_en: 'Supervisor', photo: fotoAdnan },
];

// Anggota tim
const MEMBERS = [
  { name: 'Zaidan Abdurrahman Qois, S.Hum., M.HlthInfo', role_id: 'Program, Information & Technology Consultant', role_en: 'Program, Information & Technology Consultant', photo: fotoZaidan },
  { name: 'Kenez Andro Ufairoh, S.E.', role_id: 'Finance Controller & Administrator Lead', role_en: 'Finance Controller & Administrator Lead', photo: fotoKenez },
  { name: 'Monicara Maharani, S.Hum.', role_id: 'Finance Treasurer & Technology Lead', role_en: 'Finance Treasurer & Technology Lead', photo: fotoMonica },
  { name: 'Morin Kristiani', role_id: 'Logistics Coordinator', role_en: 'Logistics Coordinator', photo: fotoMorin },
  { name: 'Lintang Shafa Mazaya, S.I.A', role_id: 'Learning & Development Coordinator', role_en: 'Learning & Development Coordinator', photo: fotoLintang },
  { name: 'Maria Vianny, S.Kep', role_id: 'Publication & Documentation Coordinator', role_en: 'Publication & Documentation Coordinator', photo: fotoMaria },
];

const TEAM_LOGOS = [
  { name: 'Youth in Public Health', src: logoYouth, url: '#' },
  { name: 'Anara.Id', src: logoAnara, url: '#' },
  { name: 'SDGs Hub UI', src: logoSDGs, url: '#' },
];
const PARTNER_LOGOS = [{ name: 'Kementerian Sosial RI', src: logoKemensos, url: 'https://kemensos.go.id' }];
const FUNDER_LOGOS = [{ name: 'ChildFund International', src: logoChildFund, url: 'https://childfund.org' }];

function initials(name) {
  const clean = name.replace(/,.*$/, '').replace(/Dr\.|S\.Hum\.|S\.E\.|S\.Kep|S\.I\.A|MA\.|M\.HlthInfo/g, '').trim();
  const p = clean.split(/\s+/).filter(Boolean);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase();
}

function PersonCard({ m, isID, featured }) {
  return (
    <div className={`${styles.card} ${featured ? styles.cardFeatured : ''}`}>
      <div className={`${styles.avatarWrap} ${featured ? styles.avatarWrapLg : ''}`}>
        <img src={m.photo} alt={m.name} className={styles.avatar} loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none'; e.currentTarget.nextSibling.style.display = 'flex'; }} />
        <span className={styles.avatarFallback} aria-hidden="true">{initials(m.name)}</span>
      </div>
      <div className={styles.name}>{m.name}</div>
      <div className={styles.role}>{isID ? m.role_id : m.role_en}</div>
    </div>
  );
}

function LogoItem({ name, src, url }) {
  return (
    <a href={url} target="_blank" rel="noreferrer" className={styles.logoLink} aria-label={name}>
      {src ? <img src={src} alt={name} /> : <span className={styles.logoText}>{name}</span>}
    </a>
  );
}

function About() {
  const { i18n } = useTranslation();
  const isID = i18n.language === 'id';

  return (
    <div className={styles.page}>
      {/* HERO */}
      <div className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.eyebrow}>{isID ? 'Tentang Kami' : 'About Us'}</div>
          <h1 className={styles.heroTitle}>{isID ? 'Tim & Mitra di Balik NOWGUT' : 'The Team & Partners Behind NOWGUT'}</h1>
          <p className={styles.heroDesc}>
            {isID
              ? 'NOWGUT dikembangkan oleh tim lintas disiplin melalui kolaborasi dengan organisasi dan mitra profesional, untuk mendukung pemantauan kesehatan gastrointestinal anak dengan ASD secara lebih baik.'
              : 'NOWGUT is developed by a cross-disciplinary team in collaboration with professional organizations and partners, to better support GI health monitoring for children with ASD.'}
          </p>
        </div>
      </div>

      <div className={styles.body}>
        {/* PIMPINAN */}
        <div className={styles.sectionLabel}>{isID ? 'Project Leadership' : 'Project Leadership'}</div>
        <div className={styles.leadGrid}>
          {LEADS.map((m, i) => <PersonCard key={i} m={m} isID={isID} featured />)}
        </div>

        {/* TIM */}
        <div className={styles.sectionLabel}>{isID ? 'Project Team' : 'Project Team'}</div>
        <div className={styles.memberGrid}>
          {MEMBERS.map((m, i) => <PersonCard key={i} m={m} isID={isID} />)}
        </div>

        {/* KOLABORASI */}
        <div className={styles.collabBlock}>
          <div className={styles.collabHeader}>
            <div className={styles.eyebrowDark}>{isID ? 'Kolaborasi Bersama' : 'In Collaboration'}</div>
            <h2 className={styles.collabTitle}>{isID ? 'Organisasi & Mitra Profesional' : 'Organizations & Professional Partners'}</h2>
          </div>
          <div className={styles.logoGroups}>
            <div className={styles.logoGroup}>
              <div className={styles.badge}><Users size={14} aria-hidden="true" />{isID ? 'Tim Pelaksana' : 'Project Team'}</div>
              <div className={styles.logoRow}>{TEAM_LOGOS.map((l, i) => <LogoItem key={i} {...l} />)}</div>
            </div>
            <div className={styles.logoGroup}>
              <div className={styles.badge}><Handshake size={14} aria-hidden="true" />{isID ? 'Mitra' : 'Partners'}</div>
              <div className={styles.logoRow}>{PARTNER_LOGOS.map((l, i) => <LogoItem key={i} {...l} />)}</div>
            </div>
            <div className={styles.logoGroup}>
              <div className={styles.badge}><Heart size={14} aria-hidden="true" />{isID ? 'Didukung oleh' : 'Supported by'}</div>
              <div className={styles.logoRow}>{FUNDER_LOGOS.map((l, i) => <LogoItem key={i} {...l} />)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default About;